import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { getProfile, logout as apiLogout } from "../features/auth/services/authService";

export const tierLimits = {
  // Individual/free count limits disabled — gating is via trial token quota instead.
  // Reinstate e.g. { projects: 1, studies: 1 } when tier-based caps return.
  free: { projects: 999, studies: 999 },
  basic: { projects: 999, studies: 999 },
  premium: { projects: 999, studies: 999 },
  enterprise: { projects: 999, studies: 999 },
  admin: { projects: 999, studies: 999 },
  super: { projects: 999, studies: 999 },
};

const tierLevels = {
  free: 0,
  basic: 1,
  premium: 2,
  enterprise: 2,
  admin: 3,
  super: 3,
};

export const normalizeUser = (userData = {}) => ({
  ...userData,
  account_type: userData.account_type || "individual",
  password_reset_required: userData.password_reset_required || false,
  company_id: userData.company_id || null,
  company_name: userData.company_name || null,
  department: userData.department || null,
  company_role: userData.company_role || userData.role || null,
});

const persistAuth = (userData) => {
  localStorage.setItem("user", JSON.stringify(userData));
  localStorage.setItem("isLoggedIn", "true");
  localStorage.removeItem("jwt");
};

const clearPersistedAuth = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("isLoggedIn");
  localStorage.removeItem("jwt");
};

export const useAuthStore = create(
  devtools(
    (set, get) => ({
      user: null,
      authToken: null,
      isLoggedIn: false,
      sessionChecked: false,

      setUser: (user) => set({ user }, false, "auth/setUser"),
      setAuthToken: (authToken) => set({ authToken }, false, "auth/setAuthToken"),
      setIsLoggedIn: (isLoggedIn) =>
        set({ isLoggedIn }, false, "auth/setIsLoggedIn"),

      hydrateFromStorage: () => {
        const savedUser = localStorage.getItem("user");
        const savedLoginState = localStorage.getItem("isLoggedIn");
        if (savedUser && savedLoginState === "true") {
          try {
            const userData = JSON.parse(savedUser);
            set(
              { user: userData, isLoggedIn: true, authToken: null },
              false,
              "auth/hydrateFromStorage"
            );
          } catch {
            // Ignore invalid persisted user data.
          }
        }
      },

      checkSession: async () => {
        get().hydrateFromStorage();
        try {
          const data = await getProfile();
          if (data.success && data.user) {
            const normalizedUserData = normalizeUser(data.user);
            persistAuth(normalizedUserData);
            set(
              {
                user: normalizedUserData,
                isLoggedIn: true,
                authToken: null,
                sessionChecked: true,
              },
              false,
              "auth/checkSessionSuccess"
            );
          } else {
            clearPersistedAuth();
            set(
              {
                user: null,
                isLoggedIn: false,
                authToken: null,
                sessionChecked: true,
              },
              false,
              "auth/checkSessionEmpty"
            );
          }
        } catch (error) {
          console.warn("세션 확인 실패:", error);
          clearPersistedAuth();
          set(
            {
              user: null,
              isLoggedIn: false,
              authToken: null,
              sessionChecked: true,
            },
            false,
            "auth/checkSessionError"
          );
        }
      },

      loginUser: (userData) => {
        const normalizedUserData = normalizeUser(userData);
        persistAuth(normalizedUserData);
        set(
          {
            user: normalizedUserData,
            isLoggedIn: true,
            authToken: null,
          },
          false,
          "auth/loginUser"
        );
      },

      logoutUser: async () => {
        try {
          await apiLogout();
        } catch (err) {
          console.warn("로그아웃 API 호출 실패 (무시):", err);
        }
        clearPersistedAuth();
        set(
          {
            user: null,
            isLoggedIn: false,
            authToken: null,
          },
          false,
          "auth/logoutUser"
        );
      },
    }),
    { name: "auth-store" }
  )
);

export const hasMinimumTierForUser = (user, requiredTier) => {
  if (!user || !user.tier) return false;

  const userLevel = tierLevels[user.tier] || 0;
  const requiredLevel = tierLevels[requiredTier] || 0;
  return userLevel >= requiredLevel;
};

export const selectHasMinimumTier = (requiredTier) => (state) => {
  return hasMinimumTierForUser(state.user, requiredTier);
};

export const getTierNameFromUser = (user) => {
  if (!user || !user.tier) return "Free";
  return user.tier.charAt(0).toUpperCase() + user.tier.slice(1);
};
